import { useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { apiService } from "@/lib/api-service";
import { OvertimeApprovalDialog } from "./OvertimeApprovalDialog";
import {
  SearchFilterBar,
  FilterSettings,
} from "@/components/ui/search-filter-bar";
import type { OvertimeEntry } from "@/types/project.types";
import { formatCurrency } from "@employee-management/common";

export function PendingApprovalsTable() {
  const [selectedEntry, setSelectedEntry] = useState<OvertimeEntry | null>(
    null
  );
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "date", desc: true },
  ]);
  const [filterSettings, setFilterSettings] = useState<FilterSettings>({
    searchTerm: "",
    filters: {},
  });

  const {
    data: rawPendingEntries = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["overtime", "pending"],
    queryFn: () => apiService.get<OvertimeEntry[]>("/overtime/pending"),
  });

  // Filter data based on filterSettings
  const pendingEntries = useMemo(() => {
    // Let's handle filtering manually since we need to map filter keys to nested paths
    let filteredEntries = [...rawPendingEntries];

    // Apply search filter
    if (filterSettings.searchTerm) {
      const searchTerm = filterSettings.searchTerm.toLowerCase();
      filteredEntries = filteredEntries.filter(
        (entry) =>
          entry.project.name.toLowerCase().includes(searchTerm) ||
          entry.project.clientName.toLowerCase().includes(searchTerm) ||
          entry.createdByRef?.name?.toLowerCase().includes(searchTerm) ||
          entry.createdByRef?.email?.toLowerCase().includes(searchTerm) ||
          entry.notes?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply filters
    Object.entries(filterSettings.filters).forEach(([key, value]) => {
      if (key === "project" && value && typeof value === "string") {
        filteredEntries = filteredEntries.filter(
          (entry) => entry.project.name === value
        );
      }
      if (key === "createdBy" && value && typeof value === "string") {
        filteredEntries = filteredEntries.filter(
          (entry) => entry.createdByRef?.name === value
        );
      }
      if (key === "dateRange" && value && typeof value === "object") {
        const dateRange = value as { min?: string; max?: string };
        filteredEntries = filteredEntries.filter((entry) => {
          const entryDate = new Date(entry.date);
          let withinRange = true;

          if (dateRange.min) {
            const minDate = new Date(dateRange.min);
            withinRange = withinRange && entryDate >= minDate;
          }

          if (dateRange.max) {
            const maxDate = new Date(dateRange.max);
            // Set max date to end of day to include the entire selected day
            maxDate.setHours(23, 59, 59, 999);
            withinRange = withinRange && entryDate <= maxDate;
          }

          return withinRange;
        });
      }
    });

    return filteredEntries;
  }, [rawPendingEntries, filterSettings]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const calculateTotalHours = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + emp.hours, 0);
  };

  const calculateTotalAmount = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + emp.total, 0);
  };

  const startActionProcess = (entry: OvertimeEntry) => {
    setSelectedEntry(entry);
    setApprovalDialogOpen(true);
  };

  const handleApprovalComplete = () => {
    setApprovalDialogOpen(false);
    setSelectedEntry(null);
    refetch();
  };

  const columns: ColumnDef<OvertimeEntry>[] = [
    {
      accessorKey: "project",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 font-semibold"
        >
          Project
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="ml-1 h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="ml-1 h-4 w-4" />
          ) : (
            <ArrowUpDown className="ml-1 h-4 w-4" />
          )}
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-sm">
          <div className="font-medium">{row.original.project.name}</div>
          <div className="text-muted-foreground">
            {row.original.project.clientName}
          </div>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.project.name.toLowerCase();
        const nameB = rowB.original.project.name.toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      accessorKey: "date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 font-semibold"
        >
          Date
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="ml-1 h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="ml-1 h-4 w-4" />
          ) : (
            <ArrowUpDown className="ml-1 h-4 w-4" />
          )}
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-medium">{formatDate(row.getValue("date"))}</div>
      ),
      sortingFn: (rowA, rowB) => {
        const dateA = new Date(rowA.getValue("date")).getTime();
        const dateB = new Date(rowB.getValue("date")).getTime();
        return dateA - dateB;
      },
    },
    {
      id: "totalHours",
      header: ({ column }) => (
        <div className="min-w-[110px] sm:min-w-[120px]">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold"
          >
            Total Hours
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="ml-1 h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="ml-1 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-1 h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="min-w-[110px] sm:min-w-[120px]">
          {calculateTotalHours(row.original.employees).toFixed(1)}h
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const hoursA = calculateTotalHours(rowA.original.employees);
        const hoursB = calculateTotalHours(rowB.original.employees);
        return hoursA - hoursB;
      },
    },
    {
      id: "totalAmount",
      header: ({ column }) => (
        <div className="min-w-[140px] sm:min-w-[120px]">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold"
          >
            Total Amount
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="ml-1 h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="ml-1 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-1 h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="min-w-[140px] sm:min-w-[120px]">
          {formatCurrency(calculateTotalAmount(row.original.employees))}
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const amountA = calculateTotalAmount(rowA.original.employees);
        const amountB = calculateTotalAmount(rowB.original.employees);
        return amountA - amountB;
      },
    },
    {
      id: "createdBy",
      header: "Created By",
      cell: ({ row }) => (
        <div className="text-sm">
          <div className="font-medium">
            {row.original.createdByRef?.name || "Unknown"}
          </div>
          <div className="text-muted-foreground">
            {row.original.createdByRef?.email || "No email"}
          </div>
        </div>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">&nbsp;</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <div className="flex items-center justify-end space-x-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => startActionProcess(row.original)}
            >
              Take Action
            </Button>
          </div>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: pendingEntries,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  if (isLoading) return <div>Loading pending approvals...</div>;
  if (isError)
    return (
      <div className="text-red-500">Failed to load pending approvals.</div>
    );

  return (
    <>
      <div className="mb-4">
        <SearchFilterBar
          placeholder="Search by project, client, creator..."
          onFilterChange={setFilterSettings}
          filterOptions={[
            {
              key: "project",
              label: "Project",
              type: "select",
              options: Array.from(
                new Set(rawPendingEntries.map((entry) => entry.project.name))
              ).map((name) => ({ label: name, value: name })),
            },
            {
              key: "createdBy",
              label: "Created By",
              type: "select",
              options: Array.from(
                new Set(
                  rawPendingEntries
                    .map((entry) => entry.createdByRef?.name)
                    .filter(Boolean)
                )
              ).map((name) => ({ label: name!, value: name! })),
            },
            {
              key: "dateRange",
              label: "Date Range",
              type: "range",
              rangeType: "date",
            },
          ]}
        />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="!bg-muted/40">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-transparent"
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  className="text-center !py-12"
                  colSpan={columns.length}
                >
                  <div className="text-muted-foreground">
                    <Check className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">
                      No pending approvals
                    </h3>
                    <p>All overtime entries have been reviewed.</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {selectedEntry && (
        <OvertimeApprovalDialog
          open={approvalDialogOpen}
          onOpenChange={setApprovalDialogOpen}
          overtimeEntry={selectedEntry}
          onComplete={handleApprovalComplete}
        />
      )}
    </>
  );
}
